import type { UserSettings } from "@screener/protocol";
import { isRecord } from "@/density/density-filters-storage";
import { DAEMON_REQUEST_TIMEOUT_MS } from "@/lib/request-timeout";
import {
  type AccountSettingsValue,
  buildFiltersSection,
  buildSettingsPayload,
  collectLegacySettings,
  defaultAccountSettings,
  filtersToSection,
  isEmptySettings,
  type RawSettings,
  readAccountSettings,
  removeLegacySettings,
  type StorageAccess,
} from "./account-settings";

/**
 * Состояние настроек аккаунта (§127, design.md stage9-account-settings, Д4 и Д5):
 * чтение при входе, кеш на случай недоступного демона, переезд из браузера и
 * прореженная запись документа целиком. Без React: часы, сеть и хранилище
 * приходят параметрами, так что вся логика проверяется без браузера.
 *
 * Правило записи: в аккаунт пишет только клиент, успешно прочитавший его
 * документ в этой сессии. Пока чтение не удалось, изменения действуют на
 * экране и ложатся в кеш, но не уходят; при переподключении потока и при
 * следующем изменении сначала повторяется чтение.
 */

const SETTINGS_URL = "/api/settings";

/** Ключ кеша: по логину, потому что в одном браузере пользователи работают по очереди (Д4). */
export const ACCOUNT_SETTINGS_CACHE_PREFIX = "screener.account.settings.";

/**
 * Записи документа идут не чаще раза в секунду, последний документ выигрывает
 * (ТЗ §127 п. 4).
 */
export const ACCOUNT_SETTINGS_WRITE_INTERVAL_MS = 1000;

/** `T` — тип идентификатора таймера окружения (число в браузере, объект в Node). */
export interface AccountSettingsDeps<T = unknown> {
  fetch: (input: string, init: RequestInit) => Promise<Response>;
  storage: StorageAccess;
  setTimer: (run: () => void, ms: number) => T;
  clearTimer: (id: T) => void;
  /** Текущее время, мс: по нему выдерживается секунда между записями. */
  now: () => number;
  /** Демон ответил `401`: сессия недействительна. */
  onSessionInvalid?: () => void;
}

export interface AccountSettingsState {
  /** `loading` — экран не показывается (§127 п. 4 «до первого показа экрана»). */
  status: "loading" | "ready";
  value: AccountSettingsValue;
  /** Последний документ ещё не принят демоном: повтор — при изменении или переподключении потока. */
  unsent: boolean;
}

type ReadResult =
  | { kind: "present"; settings: RawSettings }
  | { kind: "absent" }
  | { kind: "failed" };

/**
 * Кеш: документ и поля, изменённые после последней успешной записи. Пути —
 * `sidebar_open`, `card_row_order`, `density_filters.<поле>`.
 */
interface CacheRecord {
  document: RawSettings;
  changed: string[];
}

export interface AccountSettingsStore {
  getState: () => AccountSettingsState;
  subscribe: (listener: () => void) => () => void;
  load: () => Promise<void>;
  setFilters: (next: AccountSettingsValue["filters"]) => void;
  setRowOrder: (next: AccountSettingsValue["rowOrder"]) => void;
  setSidebarOpen: (next: boolean) => void;
  /** Поток §7.6 переподключился: чтение и неотправленный документ уходят не раньше секунды после прошлой записи. */
  notifyReconnected: () => void;
  dispose: () => void;
}

export function createAccountSettingsStore<T>(
  login: string,
  deps: AccountSettingsDeps<T>,
): AccountSettingsStore {
  const cacheKey = `${ACCOUNT_SETTINGS_CACHE_PREFIX}${login}`;
  const listeners = new Set<() => void>();
  let state: AccountSettingsState = {
    status: "loading",
    value: defaultAccountSettings,
    unsent: false,
  };
  /** Документ как показан и как будет записан (через `buildSettingsPayload`). */
  let document: RawSettings = {};
  /** Чтение аккаунта в этой сессии удалось: только тогда можно писать. */
  let readOk = false;
  /**
   * Поля, изменённые после последней успешной записи (в том числе в прошлых
   * сессиях, из кеша). Только они ложатся поверх прочитанного документа аккаунта.
   */
  const dirty = new Set<string>();
  /** Старые ключи браузера ждут удаления до успешной записи переезда. */
  let legacyPending = false;
  let version = 0;
  let inFlight = false;
  let syncing = false;
  let lastSentAt: number | null = null;
  let lastReadAt: number | null = null;
  let timer: T | null = null;
  let disposed = false;
  const controller = new AbortController();

  function setState(next: AccountSettingsState): void {
    state = next;
    for (const listener of listeners) {
      listener();
    }
  }

  function readCache(): CacheRecord | null {
    try {
      const raw = deps.storage()?.getItem(cacheKey) ?? null;
      if (raw === null) {
        return null;
      }
      const parsed: unknown = JSON.parse(raw);
      if (!isRecord(parsed) || !isRecord(parsed.document)) {
        return null;
      }
      const changed = Array.isArray(parsed.changed)
        ? parsed.changed.filter((path): path is string => typeof path === "string")
        : [];
      return { document: parsed.document, changed };
    } catch {
      return null;
    }
  }

  function writeCache(): void {
    try {
      const record: CacheRecord = {
        document: buildSettingsPayload(document),
        changed: [...dirty],
      };
      deps.storage()?.setItem(cacheKey, JSON.stringify(record));
    } catch {
      // Хранилище недоступно — кеша не будет, экран работает на памяти.
    }
  }

  async function request(method: "GET" | "PUT", body?: UserSettings): Promise<Response | null> {
    try {
      const response = await deps.fetch(SETTINGS_URL, {
        method,
        signal: AbortSignal.any([
          AbortSignal.timeout(DAEMON_REQUEST_TIMEOUT_MS),
          controller.signal,
        ]),
        ...(body === undefined
          ? {}
          : { headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }),
      });
      if (disposed) {
        return null;
      }
      if (response.status === 401) {
        deps.onSessionInvalid?.();
        return null;
      }
      return response.ok ? response : null;
    } catch {
      return null;
    }
  }

  async function read(): Promise<ReadResult> {
    lastReadAt = deps.now();
    const response = await request("GET");
    if (response === null) {
      return { kind: "failed" };
    }
    try {
      const body: unknown = await response.json();
      if (body === null) {
        return { kind: "absent" };
      }
      if (isRecord(body) && isRecord(body.settings)) {
        return { kind: "present", settings: body.settings };
      }
    } catch {
      // Не JSON (например `index.html` при рассинхроне статики и демона) — отказ чтения.
    }
    return { kind: "failed" };
  }

  /** Записывает документ целиком. Одна запись в пути: следующая ждёт её исхода. */
  async function send(): Promise<boolean> {
    inFlight = true;
    lastSentAt = deps.now();
    const sentVersion = version;
    const response = await request("PUT", buildSettingsPayload(document));
    inFlight = false;
    if (disposed) {
      return response !== null;
    }
    if (response === null) {
      return false;
    }
    if (legacyPending) {
      removeLegacySettings(deps.storage);
      legacyPending = false;
    }
    if (sentVersion === version) {
      dirty.clear();
      writeCache();
      if (state.unsent) {
        setState({ ...state, unsent: false });
      }
    } else {
      // За время записи документ изменился: нужна ещё одна, хоть эта и удалась.
      schedule();
    }
    return true;
  }

  /** Сколько ждать до следующего запроса того же рода; 0 — можно сейчас. */
  function waitSince(last: number | null): number {
    return last === null ? 0 : Math.max(0, last + ACCOUNT_SETTINGS_WRITE_INTERVAL_MS - deps.now());
  }

  /** Пишет сейчас или, если с прошлой записи не прошла секунда, ставит в очередь. */
  async function sendThrottled(): Promise<void> {
    if (inFlight || disposed) {
      return;
    }
    const wait = waitSince(lastSentAt);
    if (wait > 0) {
      schedule(wait);
      return;
    }
    await send();
  }

  function schedule(delayMs: number = ACCOUNT_SETTINGS_WRITE_INTERVAL_MS): void {
    if (timer !== null || disposed) {
      return;
    }
    timer = deps.setTimer(() => {
      timer = null;
      void sync();
    }, delayMs);
  }

  /** Чтение, если оно ещё не удалось, затем запись неотправленного. */
  async function sync(): Promise<void> {
    if (syncing || disposed) {
      return;
    }
    syncing = true;
    try {
      if (!readOk) {
        const wait = waitSince(lastReadAt);
        if (wait > 0) {
          schedule(wait);
          return;
        }
        if (!(await attemptRead())) {
          return;
        }
      }
      if (state.unsent) {
        await sendThrottled();
      }
    } finally {
      syncing = false;
    }
  }

  /** Читает аккаунт и сводит с ним локальное состояние; `false` — чтение не удалось. */
  async function attemptRead(): Promise<boolean> {
    const result = await read();
    if (disposed || result.kind === "failed") {
      return false;
    }
    reconcile(result);
    return true;
  }

  /** Поле, изменённое локально, ложится поверх прочитанного; остальные поля раздела остаются аккаунтовы. */
  function overlay(target: RawSettings, path: string): void {
    const filterPrefix = "density_filters.";
    if (!path.startsWith(filterPrefix)) {
      target[path] = document[path];
      return;
    }
    const local = document.density_filters;
    if (!isRecord(local)) {
      return;
    }
    const section = isRecord(target.density_filters) ? { ...target.density_filters } : {};
    section[path.slice(filterPrefix.length)] = local[path.slice(filterPrefix.length)];
    target.density_filters = section;
  }

  /**
   * Сводит прочитанный документ аккаунта с локальным: от локального берутся
   * только поля, изменённые после последней успешной записи (в этой сессии или
   * в кеше прошлой), всё остальное — из аккаунта.
   */
  function reconcile(
    result: { kind: "present"; settings: RawSettings } | { kind: "absent" },
  ): void {
    let next: RawSettings;
    let migrating = false;
    if (result.kind === "present") {
      next = { ...result.settings };
      removeLegacySettings(deps.storage);
      legacyPending = false;
    } else {
      // Записи нет: настроенное в браузере переезжает в аккаунт один раз (§127 п. 5).
      const legacy = collectLegacySettings(deps.storage);
      next = { ...legacy };
      migrating = !isEmptySettings(legacy);
      legacyPending = migrating;
    }
    for (const path of dirty) {
      overlay(next, path);
    }
    document = next;
    readOk = true;
    const unsent = dirty.size > 0 || (result.kind === "absent" && !isEmptySettings(next));
    if (unsent) {
      version += 1;
    }
    writeCache();
    setState({ status: "ready", value: readAccountSettings(document), unsent });
  }

  /** Чтение не удалось: показ из кеша этого логина, иначе из старых ключей, иначе по умолчанию. */
  function showFallback(cached: CacheRecord | null): void {
    if (cached !== null) {
      document = cached.document;
    } else {
      document = { ...collectLegacySettings(deps.storage) };
    }
    readOk = false;
    setState({ status: "ready", value: readAccountSettings(document), unsent: dirty.size > 0 });
  }

  function change(
    value: Partial<AccountSettingsValue>,
    key: string,
    section: unknown,
    paths: string[],
  ): void {
    if (state.status !== "ready" || disposed) {
      return;
    }
    document = { ...document, [key]: section };
    for (const path of paths) {
      dirty.add(path);
    }
    version += 1;
    writeCache();
    setState({ ...state, value: { ...state.value, ...value }, unsent: true });
    schedule();
  }

  async function load(): Promise<void> {
    syncing = true;
    try {
      const cached = readCache();
      if (cached !== null) {
        document = cached.document;
        for (const path of cached.changed) {
          dirty.add(path);
        }
      }
      if (!(await attemptRead())) {
        if (!disposed) {
          showFallback(cached);
        }
        return;
      }
      if (state.unsent) {
        await sendThrottled();
      }
    } finally {
      syncing = false;
    }
  }

  return {
    getState: () => state,
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    load,
    setFilters(next) {
      const before = filtersToSection(state.value.filters);
      const after = filtersToSection(next);
      const paths = Object.keys(after)
        .filter(
          (field) =>
            JSON.stringify(before[field as keyof typeof before]) !==
            JSON.stringify(after[field as keyof typeof after]),
        )
        .map((field) => `density_filters.${field}`);
      change(
        { filters: next },
        "density_filters",
        buildFiltersSection(document.density_filters, next),
        paths,
      );
    },
    setRowOrder(next) {
      change({ rowOrder: next }, "card_row_order", next, ["card_row_order"]);
    },
    setSidebarOpen(next) {
      change({ sidebarOpen: next }, "sidebar_open", next, ["sidebar_open"]);
    },
    notifyReconnected() {
      if (disposed || syncing || inFlight || state.status !== "ready") {
        return;
      }
      if (readOk && !state.unsent) {
        return;
      }
      void sync();
    },
    dispose() {
      disposed = true;
      controller.abort();
      if (timer !== null) {
        deps.clearTimer(timer);
        timer = null;
      }
      listeners.clear();
    },
  };
}
